'use client';

import { useEffect, useMemo, useState } from 'react';
import { Library, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MaskedSvgIcon } from '@/components/help-center/MaskedSvgIcon';
import { GsImage } from '@/components/help-center/latest-power-shared';
import { Icon } from '@/components/icons/Icon';
import { IconLibraryPicker } from '@/components/painel/icon-library/IconLibraryPicker';
import { PainelPermissionsDialogShell } from '@/components/painel/permissions/painel-role-visuals';
import {
  painelButtonPrimaryClass,
  painelModalCancelButtonClass,
} from '@/components/painel/painel-styles';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { PowerSectionSmileyIcon } from '@/components/help-center/powers/power-gallery-view';
import {
  LORE_CURIOSITY_COLORFUL_ICONS,
  LORE_CURIOSITY_TINTED_ICONS,
} from '@/lib/lore-curiosity-icons';
import { isLibraryIconRef, toLibraryIconRef } from '@/lib/icon-library';
import { cn } from '@/lib/utils';

type PickerMode = 'choose' | 'smiley' | 'icon' | 'library';

type CuriosityIconPickerModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Valor atual (código smiley ou path `/icons/...`). */
  value?: string;
  onConfirm: (next: string) => void;
};

function isIconPath(value: string): boolean {
  const v = value.trim();
  return v.startsWith('/') || v.startsWith('icons/') || /\.svg$/i.test(v);
}

function resolveInitialMode(value: string): PickerMode {
  const v = value.trim();
  if (!v) return 'choose';
  if (isLibraryIconRef(v)) return 'library';
  return isIconPath(v) ? 'icon' : 'smiley';
}

const choiceCardBase =
  'flex flex-col items-center gap-3 rounded-2xl border p-5 text-center transition';

function IconGridButton({
  path,
  label,
  active,
  tinted,
  onSelect,
}: {
  path: string;
  label: string;
  active: boolean;
  tinted: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onSelect}
      className={cn(
        'flex flex-col items-center gap-2 rounded-xl border p-3 transition',
        active
          ? 'border-amber-400/50 bg-amber-500/15 ring-1 ring-amber-400/40'
          : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.06]'
      )}
    >
      {tinted ? (
        <MaskedSvgIcon src={path} className="h-8 w-8 text-slate-400" />
      ) : (
        <img src={path} alt="" className="h-8 w-8 object-contain" draggable={false} />
      )}
      <span className="line-clamp-1 font-inter text-[10px] text-slate-400">{label}</span>
    </button>
  );
}

export function CuriosityIconPickerModal({
  open,
  onOpenChange,
  value = '',
  onConfirm,
}: CuriosityIconPickerModalProps) {
  const initialMode = useMemo<PickerMode>(() => resolveInitialMode(value), [value]);

  const [mode, setMode] = useState<PickerMode>(initialMode);
  const [smiley, setSmiley] = useState(() =>
    isIconPath(value) || isLibraryIconRef(value) ? '' : value.replace(/[()]/g, '').trim()
  );
  const [iconPath, setIconPath] = useState(() => (isIconPath(value) ? value.trim() : ''));
  const [libraryRef, setLibraryRef] = useState(() =>
    isLibraryIconRef(value) ? value.trim() : ''
  );
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const v = value.trim();
    if (!v) {
      setMode('choose');
      setSmiley('');
      setIconPath('');
      setLibraryRef('');
      return;
    }
    if (isLibraryIconRef(v)) {
      setMode('library');
      setLibraryRef(v);
      setSmiley('');
      setIconPath('');
      return;
    }
    if (isIconPath(v)) {
      setMode('icon');
      setIconPath(v);
      setSmiley('');
      setLibraryRef('');
    } else {
      setMode('smiley');
      setSmiley(v.replace(/[()]/g, '').trim());
      setIconPath('');
      setLibraryRef('');
    }
  }, [open, value]);

  const smileyPreview = smiley.trim() || 'smile';

  const canConfirm =
    mode === 'smiley'
      ? Boolean(smiley.trim())
      : mode === 'icon'
        ? Boolean(iconPath.trim())
        : mode === 'library'
          ? Boolean(libraryRef.trim())
          : false;

  const handleConfirm = () => {
    if (mode === 'smiley' && smiley.trim()) {
      onConfirm(smiley.trim().replace(/[()]/g, ''));
      onOpenChange(false);
      return;
    }
    if (mode === 'icon' && iconPath.trim()) {
      onConfirm(iconPath.trim());
      onOpenChange(false);
      return;
    }
    if (mode === 'library' && libraryRef.trim()) {
      onConfirm(libraryRef.trim());
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[90vh] max-w-lg flex-col overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
      >
        <DialogTitle className="sr-only">Ícone da curiosidade</DialogTitle>
        <PainelPermissionsDialogShell className="min-h-0 flex-1 overflow-hidden">
          <div className="shrink-0 border-b border-white/10 bg-blue-950/30 px-6 pb-5 pt-6 sm:px-8">
            <div className="flex items-start gap-4 pr-14">
              <span
                className={cn(
                  'inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl',
                  'bg-gradient-to-br from-sky-500/25 to-blue-700/10 ring-1 ring-sky-400/35',
                  'shadow-[0_0_40px_-10px_rgba(56,189,248,0.35)]'
                )}
              >
                <Sparkles className="h-6 w-6 text-sky-200" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
                  Curiosidade
                </p>
                <h2 className="mt-1 font-poppins text-xl font-bold text-white sm:text-2xl">
                  Escolher ícone
                </h2>
                <p className="mt-1.5 font-inter text-sm leading-relaxed text-slate-400">
                  Use um smiley do xat ou um ícone da biblioteca do Help Center.
                </p>
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5 sm:px-8">
            {mode === 'choose' ? (
              <div className="grid gap-3 sm:grid-cols-3">
                <button
                  type="button"
                  onClick={() => setMode('smiley')}
                  className={cn(
                    choiceCardBase,
                    'border-amber-400/55 bg-amber-500/[0.10] hover:border-amber-400/80 hover:bg-amber-500/[0.16]',
                    'ring-1 ring-amber-400/20'
                  )}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-amber-400/50 bg-amber-400/15">
                    <GsImage
                      code="smile"
                      className="flex h-9 w-9 items-center justify-center"
                      imgClassName="max-h-9 max-w-9 object-contain"
                    />
                  </span>
                  <span className="font-poppins text-sm font-semibold text-amber-200">Smiley</span>
                  <span className="font-inter text-xs text-amber-200/65">
                    Digite o código e veja o preview
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setMode('icon')}
                  className={cn(
                    choiceCardBase,
                    'border-sky-400/55 bg-sky-500/[0.10] hover:border-sky-400/80 hover:bg-sky-500/[0.16]',
                    'ring-1 ring-sky-400/20'
                  )}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-sky-400/50 bg-sky-400/15">
                    <MaskedSvgIcon src="/icons/smile.svg" className="h-7 w-7 text-sky-300" />
                  </span>
                  <span className="font-poppins text-sm font-semibold text-sky-200">Ícone</span>
                  <span className="font-inter text-xs text-sky-200/65">
                    Escolha um SVG do Help Center
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMode('library');
                    setLibraryPickerOpen(true);
                  }}
                  className={cn(
                    choiceCardBase,
                    'border-violet-400/55 bg-violet-500/[0.10] hover:border-violet-400/80 hover:bg-violet-500/[0.16]',
                    'ring-1 ring-violet-400/20'
                  )}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-violet-400/50 bg-violet-400/15">
                    <Library className="h-7 w-7 text-violet-300" />
                  </span>
                  <span className="font-poppins text-sm font-semibold text-violet-200">Biblioteca</span>
                  <span className="font-inter text-xs text-violet-200/65">
                    Ícones da Icon Library
                  </span>
                </button>
              </div>
            ) : null}

            {mode === 'smiley' ? (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => setMode('choose')}
                  className="font-inter text-xs text-sky-300/80 hover:text-sky-200"
                >
                  ← Voltar
                </button>
                <div className="space-y-2">
                  <Label className="font-poppins text-slate-400">Código do smiley</Label>
                  <Input
                    value={smiley}
                    onChange={(e) => setSmiley(e.target.value.replace(/[()]/g, ''))}
                    placeholder="ex.: smile, topman, purple"
                    className={cn(painelInputClass, 'font-mono')}
                  />
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-amber-400/25 bg-amber-400/10">
                    <PowerSectionSmileyIcon code={smileyPreview} size="md" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-poppins text-xs font-semibold text-slate-300">Preview</p>
                    <p className="truncate font-mono text-xs text-slate-500">({smileyPreview})</p>
                  </div>
                </div>
              </div>
            ) : null}

            {mode === 'icon' ? (
              <div className="space-y-5">
                <button
                  type="button"
                  onClick={() => setMode('choose')}
                  className="font-inter text-xs text-sky-300/80 hover:text-sky-200"
                >
                  ← Voltar
                </button>

                <div className="space-y-2">
                  <p className="font-poppins text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-300/70">
                    Ícones
                  </p>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {LORE_CURIOSITY_TINTED_ICONS.map((item) => (
                      <IconGridButton
                        key={item.path}
                        path={item.path}
                        label={item.label}
                        active={iconPath === item.path}
                        tinted
                        onSelect={() => setIconPath(item.path)}
                      />
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="font-poppins text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-300/70">
                    Ícones coloridos
                  </p>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {LORE_CURIOSITY_COLORFUL_ICONS.map((item) => (
                      <IconGridButton
                        key={item.path}
                        path={item.path}
                        label={item.label}
                        active={iconPath === item.path}
                        tinted={false}
                        onSelect={() => setIconPath(item.path)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
            {mode === 'library' ? (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => setMode('choose')}
                  className="font-inter text-xs text-sky-300/80 hover:text-sky-200"
                >
                  ← Voltar
                </button>
                <div className="flex items-center gap-3 rounded-xl border border-violet-400/25 bg-violet-500/[0.08] p-3">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-violet-400/30 bg-violet-400/10 text-violet-200">
                    {libraryRef ? (
                      <Icon
                        name={libraryRef.replace(/^library:/i, '')}
                        className="h-8 w-8"
                      />
                    ) : (
                      <Library className="h-6 w-6" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-poppins text-xs font-semibold text-slate-300">
                      {libraryRef || 'Nenhum ícone selecionado'}
                    </p>
                    <p className="font-inter text-xs text-slate-500">Referência library:slug</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-xl border-white/15 bg-white/[0.04] text-sky-100"
                    onClick={() => setLibraryPickerOpen(true)}
                  >
                    Escolher
                  </Button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="shrink-0 flex flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/40 px-6 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-8">
            <Button
              type="button"
              variant="ghost"
              className={painelModalCancelButtonClass}
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            {mode !== 'choose' ? (
              <Button
                type="button"
                disabled={!canConfirm}
                className={cn(painelButtonPrimaryClass, 'h-10 px-5')}
                onClick={handleConfirm}
              >
                Usar ícone
              </Button>
            ) : null}
          </div>
        </PainelPermissionsDialogShell>
      </DialogContent>

      <IconLibraryPicker
        open={libraryPickerOpen}
        onOpenChange={setLibraryPickerOpen}
        onSelect={(item) => {
          setLibraryRef(toLibraryIconRef(item.slug));
          setMode('library');
        }}
      />
    </Dialog>
  );
}
